import { Directive, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { downloadElement } from './pro-targeting/composables/utils'
import { useTargetingListBuilder } from './pro-targeting/composables/targetingLists'
import { useAnalyzeStore } from './pro-targeting/stores/analyze'
import { i18n } from './i18n'
import { usePersonalStore } from './pro-targeting/stores/personal'

export const yFade: Directive = {
  mounted(el, binding) {
    const fadeAmount = binding.value ?? 12
    let isScrollable
    let isScrolledToBottom
    let isScrolledToTop

    function onScroll(viaTimer = false) {
      if (viaTimer) intervalCount++
      if (intervalCount > 100) {
        clearInterval(timer)
      }
      isScrollable = el.scrollHeight > el.clientHeight

      if (!isScrollable) {
        el.classList.remove(`is-bottom-overflowing-${fadeAmount}`, `is-top-overflowing-${fadeAmount}`)
        el.classList.remove('fade-y-transparent')
        return
      }

      el.classList.add('fade-y-transparent')

      isScrolledToBottom = el.scrollHeight < el.clientHeight + el.scrollTop + 2
      isScrolledToTop = isScrolledToBottom ? false : el.scrollTop === 0
      el.classList.toggle(`is-bottom-overflowing-${fadeAmount}`, !isScrolledToBottom)
      el.classList.toggle(`is-top-overflowing-${fadeAmount}`, !isScrolledToTop)
    }

    const timer = setInterval(() => onScroll(true), 200)
    let intervalCount = 0

    el.addEventListener('scroll', onScroll)
    el._scrollHandler = onScroll
  },
  beforeUnmount(el) {
    if (el._scrollHandler) {
      el.removeEventListener('scroll', el._scrollHandler)
      delete el._scrollHandler
    }
  },
}

function changeIcon(targetElement: HTMLElement) {
  const icon = Array.from(targetElement.querySelectorAll('.dodo-icon')).find(
    (e) => (e as HTMLElement).innerText === 'open_in_full',
  )
  if (icon) {
    ;(icon as HTMLElement).innerText = 'close_fullscreen'
    return
  }

  const icon2 = Array.from(targetElement.querySelectorAll('.dodo-icon')).find(
    (e) => (e as HTMLElement).innerText === 'close_fullscreen',
  )
  if (icon2) {
    ;(icon2 as HTMLElement).innerText = 'open_in_full'
    return
  }
}

function addPoweredBy(targetElement: HTMLElement) {
  const { t } = i18n.global
  const existing = targetElement?.querySelector('#existingPoweredBy')
  if (existing && existing instanceof HTMLDivElement) {
    existing.classList.remove('hidden')
    return
  }

  if (!targetElement) return

  targetElement.classList.add('relative')
  const poweredByLabel = t('powered_by_sdb')
  const poweredBy = document.createElement('div')
  poweredBy.id = 'poweredBy'
  poweredBy.innerHTML = `<div class="absolute top-20 -translate-y-1 left-8 text-sm text-gray-600 dark:text-gray-300">${poweredByLabel}</div>`
  targetElement.prepend(poweredBy)
}

function removePoweredBy(targetElement: HTMLElement) {
  const existing = targetElement?.querySelector('#existingPoweredBy')
  if (existing && existing instanceof HTMLDivElement) {
    existing.classList.add('hidden')
    return
  }

  if (!targetElement) return

  targetElement.classList.remove('relative')
  targetElement.querySelector('#poweredBy')?.remove()
}

export const col: Directive = {
  mounted(el, binding) {
    let isExpanded = false
    el.classList.add('cursor-pointer')

    let targetElement
    if (binding.value?.querySelector) {
      const querySelector = binding.value.querySelector
      targetElement = document.querySelector(querySelector)
    } else {
      targetElement = el
    }
    const targetClass = binding.value?.span ?? 'col-span-2'

    const clickHandler = () => {
      if (binding.value?.expanded !== undefined) {
        isExpanded = binding.value.expanded
      }
      isExpanded = !isExpanded
      targetElement.classList.toggle(targetClass, isExpanded)

      if (isExpanded) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' })
        addPoweredBy(targetElement)
        changeIcon(targetElement)
        if (binding.value !== undefined) {
          binding.value.expanded = true
        }
      } else {
        removePoweredBy(targetElement)
        changeIcon(targetElement)
        if (binding.value !== undefined) {
          binding.value.expanded = false
        }
      }
    }

    el.addEventListener('click', clickHandler)

    el._clickHandler = clickHandler
  },
  beforeUnmount(el) {
    if (el._clickHandler) {
      el.removeEventListener('click', el._clickHandler)
      delete el._clickHandler
    }
  },
}

export const constantAttention: Directive = {
  mounted(el, binding) {
    function isInViewport(element: HTMLElement): boolean {
      const rect = element.getBoundingClientRect()
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      )
    }
    function scrollToEnd() {
      el.scrollIntoView({ behavior: 'auto', block: 'end', inline: 'end' })
    }

    if (!isInViewport(el)) {
      scrollToEnd()
    }

    const mutationObserver = new MutationObserver(() => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20 // allow for rounding errors
      if (!atBottom && binding.value.enabled !== false) {
        scrollToEnd()
      }
    })

    mutationObserver.observe(el, { childList: true, subtree: true, characterData: true })

    el._mutationObserver = mutationObserver
  },
  beforeUnmount(el) {
    if (el._mutationObserver) {
      el._mutationObserver.disconnect()
      delete el._mutationObserver
    }
  },
}

export const attentionGrabbing: Directive = {
  mounted(el) {
    const isInViewport = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect()
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      )
    }

    if (!isInViewport(el)) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
    }
  },
}

function adjustMaxHeight(node: Node | null) {
  if (!node) return

  if (node && node instanceof Element) {
    const fadeElement = node.querySelector('.fade-y-transparent') as HTMLElement
    if (fadeElement) {
      fadeElement.classList.add('max-h-full')
      return
    }

    const maxHeightElement = node.querySelector('.max-h-105') as HTMLElement
    if (maxHeightElement) {
      maxHeightElement.classList.remove('overflow-y-auto')
      maxHeightElement.classList.add('max-h-full')
      return
    }

    adjustMaxHeight(node.parentNode)
  }
}

function restrictMaxHeight() {
  const fadeElement = Array.from(document.querySelectorAll('.fade-y-transparent.max-h-full')) as HTMLElement[]
  fadeElement.forEach((e) => {
    e.classList.remove('max-h-full')
  })

  const maxHeightElements = Array.from(document.querySelectorAll('.max-h-105.max-h-full')) as HTMLElement[]
  maxHeightElements.forEach((e) => {
    e.classList.remove('max-h-full')
    e.classList.add('overflow-y-auto')
  })
}

function parentKeepBackground(n: Node) {
  if (n && 'getAttribute' in n && typeof n.getAttribute === 'function' && n.getAttribute('data-keep-background')) {
    return true
  }

  if (n && 'parentNode' in n && n.parentNode) {
    return parentKeepBackground(n.parentNode)
  }

  return false
}

const opts = {
  adjustClonedNode: (node: Node) => {
    adjustMaxHeight(node)

    if (parentKeepBackground(node)) {
      return node
    }

    return node
  },
  filter: (node: Node) => {
    if (
      node &&
      'dataset' in node &&
      typeof node.dataset === 'object' &&
      node.dataset &&
      'skipDownload' in node.dataset &&
      node.dataset['skipDownload'] === 'true'
    ) {
      return false
    }

    if (
      node &&
      'classList' in node &&
      typeof node.classList === 'object' &&
      node.classList &&
      'contains' in node.classList &&
      typeof node.classList.contains === 'function' &&
      node.classList.contains('dodo-icon')
    ) {
      return false
    }

    return true
  },
}

export const download: Directive = {
  mounted(el, binding) {
    const { withContinuingUserActivityEntry } = useTargetingListBuilder()
    el.classList.add('cursor-pointer')

    let targetElement
    if (binding.value?.querySelector) {
      const querySelector = binding.value.querySelector
      targetElement = document.querySelector(querySelector)
    } else {
      targetElement = el
    }

    const clickHandler = async () => {
      const downloadNameExtra = el.dataset.downloadnameextra ?? targetElement.dataset.downloadnameextra
      const name = binding.value?.name
        ? binding.value.name + (downloadNameExtra ? `_${downloadNameExtra}` : '')
        : 'socialdatabase_download'
      addPoweredBy(targetElement)
      const targetClass = binding.value?.span ?? 'col-span-2'
      targetElement.classList.add(targetClass)
      try {
        const otherOptions = el.dataset?.downloadoptions ? JSON.parse(el.dataset.downloadoptions) : {}
        await withContinuingUserActivityEntry(
          downloadElement,
          'Download single image ' + name + `_${downloadNameExtra}`,
          targetElement,
          name,
          {
            ...opts,
            ...otherOptions,
          },
        )
      } finally {
        restrictMaxHeight()
        targetElement.classList.remove(targetClass)
        removePoweredBy(targetElement)
      }
    }

    el.addEventListener('click', clickHandler)

    el._clickHandler = clickHandler
  },
  beforeUnmount(el) {
    if (el._clickHandler) {
      el.removeEventListener('click', el._clickHandler)
      delete el._clickHandler
    }
  },
}

const globalEventSource = ref()
export const askGrok: Directive = {
  mounted(el, binding) {
    const { withContinuingUserActivityEntry } = useTargetingListBuilder()

    const personalStore = usePersonalStore()
    const { locale } = storeToRefs(personalStore)

    const analyzeStore = useAnalyzeStore()
    const { isOpenAi, aiReferences, aiReferencesLoading } = storeToRefs(analyzeStore)

    el.classList.add('cursor-pointer')

    const responseSummary = ref<string[]>([])

    let targetElement
    if (binding.value?.querySelector) {
      const querySelector = binding.value.querySelector
      targetElement = document.querySelector(querySelector)
    } else {
      targetElement = el
    }

    function fetchSummaryStream(url: string, payload: Record<string, any>) {
      const pathSummary = url
      const eventSource = new EventSource(pathSummary, {
        withCredentials: true, // Not applicable, but ensure you're aware of cross-origin requests
        // @ts-ignore EventSource is overrided by SSE in main.ts
        payload,
        headers: {
          'Content-Type': 'application/json',
        },
      })

      responseSummary.value = []
      globalEventSource.value?.close()
      aiReferences.value.push({ response: '', type: 'response' })
      eventSource.onmessage = (event) => {
        if (event.data === '[END]') {
          aiReferencesLoading.value = false
          eventSource.close() // close stream when done
          globalEventSource.value = undefined
        } else {
          ;(responseSummary.value as any[]).push(event.data)
          // @ts-ignore findLastIndex is available to modern browsers, TODO need to update target lib in tsconfig
          aiReferences.value[aiReferences.value.findLastIndex((a) => a.type === 'response')] = {
            response: responseSummary.value.join(''),
            type: 'response',
          }
        }
      }

      eventSource.onerror = () => {
        aiReferencesLoading.value = false
        eventSource.close() // Close the stream if an error occurs
        globalEventSource.value = undefined
      }

      return eventSource
    }

    const clickHandler = async (event: MouseEvent) => {
      event.stopPropagation()
      const data = el.dataset.data ?? targetElement.dataset.data ?? {}
      const url =
        el.dataset.url ??
        targetElement.dataset.url ??
        'https://api.socialdatabase.com/api/external/analyze-chat-query/?'
      isOpenAi.value = true
      aiReferencesLoading.value = true
      aiReferences.value = []
      aiReferences.value.push({ type: 'question', context: JSON.parse(data).query })
      aiReferences.value.push({ ...JSON.parse(data), type: 'context', locale: locale.value })
      delete aiReferences.value[aiReferences.value.length - 1].response
      delete aiReferences.value[aiReferences.value.length - 1].query
      delete aiReferences.value[aiReferences.value.length - 1].model
      withContinuingUserActivityEntry(fetchSummaryStream, 'Ask grok', url, data)
    }
    el.addEventListener('click', clickHandler)

    el._clickHandler = clickHandler
  },
  beforeUnmount(el) {
    if (el._clickHandler) {
      el.removeEventListener('click', el._clickHandler)
      delete el._clickHandler
    }
  },
}
